import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fmtDateTime, fmtTime } from "../../lib/time";
import api, { formatApiError } from "../../lib/api";
import { fmtBought, unitsBought } from "../../lib/surveyFmt";
import { toast } from "sonner";
import { SelfieThumb } from "../../components/SelfieThumb";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { SurveyEditDialog } from "./SurveyEditDialog";
import {
  ArrowLeft,
  LogIn,
  LogOut as LogOutIcon,
  ClipboardList,
  Package,
  MapPin,
  Loader2,
  Pencil,
  X,
  Check,
} from "lucide-react";

const TYPE_META = {
  check_in: { icon: LogIn, label: "Check-in", color: "border-emerald-500 text-emerald-600" },
  check_out: { icon: LogOutIcon, label: "Check-out", color: "border-red-500 text-red-600" },
  stock: { icon: Package, label: "Stock", color: "border-slate-400/80 text-slate-800" },
  survey: { icon: ClipboardList, label: "Survey", color: "border-neutral-400 text-neutral-700" },
};

export default function ShiftDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingStock, setEditingStock] = useState(false);
  const [stockQty, setStockQty] = useState({});
  const [savingStock, setSavingStock] = useState(false);
  const [editSurvey, setEditSurvey] = useState(null);
  const [forcing, setForcing] = useState(false);

  const load = useCallback(() => {
    return api
      .get(`/admin/shifts/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-neutral-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading shift…
      </div>
    );
  }
  if (!data) return null;

  const { shift, promoter, store, surveys, stock_entries, timeline } = data;
  const stock = stock_entries[stock_entries.length - 1]; // last/most-recent

  const forceCheckout = async () => {
    if (!window.confirm("Force check-out this shift now?")) return;
    setForcing(true);
    try {
      await api.post(`/admin/shifts/${id}/force-checkout`);
      toast.success("Shift checked out");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setForcing(false);
    }
  };

  const startEditStock = () => {
    const q = {};
    (stock?.sku_details || []).forEach((sd) => {
      if (sd.sku?.id) q[sd.sku.id] = sd.qty;
    });
    setStockQty(q);
    setEditingStock(true);
  };

  const saveStock = async () => {
    setSavingStock(true);
    try {
      const quantities = {};
      for (const [sid, v] of Object.entries(stockQty)) {
        const n = parseInt(v, 10);
        if (Number.isNaN(n) || n < 0) {
          toast.error("Quantities must be non-negative numbers");
          setSavingStock(false);
          return;
        }
        quantities[sid] = n;
      }
      await api.patch(`/admin/stock-entries/${stock.id}`, { quantities });
      toast.success("Stock updated");
      setEditingStock(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingStock(false);
    }
  };

  return (
    <div className="space-y-8">
      <button
        onClick={() => nav(-1)}
        className="flex items-center gap-2 text-sm text-neutral-500 hover:text-black"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back to reports
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Shift detail
          </div>
          <h1 className="font-display font-semibold text-3xl tracking-tight mt-1">
            {promoter?.name || "Promoter"} · {store?.name || "Store"}
          </h1>
          <div className="text-sm text-neutral-500 mt-1 font-mono">
            {fmtDateTime(shift.check_in_at)} →{" "}
            {shift.check_out_at ? fmtDateTime(shift.check_out_at) : "active"}
            {shift.check_out_method === "admin_forced" && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-widest border border-amber-500 text-amber-600">
                forced by admin
              </span>
            )}
            {shift.acknowledged_at && (
              <span
                className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-widest border border-emerald-400 text-emerald-700 rounded-full"
                data-testid="shift-ack-chip"
              >
                Guidelines acknowledged {fmtTime(shift.acknowledged_at)}
              </span>
            )}
          </div>
        </div>
        {shift.status === "active" && (
          <Button
            variant="outline"
            className="rounded-xl border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={forceCheckout}
            disabled={forcing}
            data-testid="force-checkout-button"
          >
            {forcing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <LogOutIcon className="h-4 w-4 mr-1" /> Force check-out
              </>
            )}
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 glass-panel divide-x divide-slate-200/60">
        <div className="p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Status
          </div>
          <div className="font-display font-bold text-lg mt-2">{shift.status}</div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Surveys
          </div>
          <div className="font-display font-bold text-lg mt-2">{surveys.length}</div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Check-in distance
          </div>
          <div className="font-display font-bold text-lg mt-2 font-mono">
            {shift.check_in_distance_m != null ? `${Math.round(shift.check_in_distance_m)}m` : "—"}
          </div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Check-out distance
          </div>
          <div className="font-display font-bold text-lg mt-2 font-mono">
            {shift.check_out_distance_m != null
              ? `${Math.round(shift.check_out_distance_m)}m`
              : "—"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-xl tracking-tight mb-3">Timeline</h2>
          <div className="border-l-2 border-slate-300/60 pl-6 space-y-5 ml-2">
            {timeline.map((e, i) => {
              const meta = TYPE_META[e.type] || TYPE_META.survey;
              const Icon = meta.icon;
              return (
                <div key={`${e.type}-${e.at || i}-${e.ref_id || ""}`} className="relative" data-testid={`timeline-${e.type}`}>
                  <div
                    className={`absolute -left-[34px] w-8 h-8 bg-white border ${meta.color} flex items-center justify-center`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {meta.label} · {fmtTime(e.at)}
                  </div>
                  <div className="text-sm mt-1 font-medium">{e.label}</div>
                  {e.distance_m != null && (
                    <div className="text-xs text-neutral-500 font-mono mt-0.5 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {Math.round(e.distance_m)}m from store
                    </div>
                  )}
                  {e.photo && (
                    <div className="mt-2">
                      <SelfieThumb path={e.photo} size={96} alt={meta.label} />
                    </div>
                  )}
                </div>
              );
            })}
            {timeline.length === 0 && (
              <div className="text-sm text-neutral-500">No events recorded.</div>
            )}
          </div>
        </div>

        {/* Sidebar: stock + promoter */}
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-xl tracking-tight">Stock count</h2>
              {stock && !editingStock && (
                <button
                  onClick={startEditStock}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-slate-300/70 hover:bg-white/80"
                  data-testid="edit-stock-button"
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
              )}
              {stock && editingStock && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingStock(false)}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-slate-300/70 hover:bg-white/80"
                    data-testid="cancel-stock-edit"
                  >
                    <X className="h-3 w-3" /> Cancel
                  </button>
                  <button
                    onClick={saveStock}
                    disabled={savingStock}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest bg-slate-900 text-white hover:bg-slate-700"
                    data-testid="save-stock-edit"
                  >
                    {savingStock ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <Check className="h-3 w-3" /> Save
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            {stock ? (
              <div className="glass-panel divide-y divide-slate-200/50">
                {stock.sku_details.map((sd) => (
                  <div key={sd.sku?.id || sd.sku?.name || JSON.stringify(sd)} className="flex items-center justify-between p-3">
                    <span className="text-sm">{sd.sku?.name || "?"}</span>
                    {editingStock && sd.sku?.id ? (
                      <Input
                        type="number"
                        min="0"
                        className="rounded-xl w-20 h-8 text-right font-mono"
                        value={stockQty[sd.sku.id] ?? ""}
                        onChange={(e) =>
                          setStockQty((q) => ({ ...q, [sd.sku.id]: e.target.value }))
                        }
                        data-testid={`stock-edit-input-${sd.sku.id}`}
                      />
                    ) : (
                      <span className="font-mono font-semibold">{sd.qty}</span>
                    )}
                  </div>
                ))}
                <div className="p-2 bg-white/50 font-mono text-[10px] uppercase tracking-widest text-neutral-500 text-center">
                  Recorded {fmtTime(stock.created_at)}
                  {stock.updated_at && " · edited"}
                </div>
              </div>
            ) : (
              <div className="glass-panel p-4 text-sm text-neutral-500">
                No stock recorded this shift.
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display font-bold text-xl tracking-tight mb-3">Promoter</h2>
            <div className="glass-panel p-4 space-y-2 text-sm">
              <div>
                <span className="text-neutral-500">Name:</span> {promoter?.name}
              </div>
              <div>
                <span className="text-neutral-500">Email:</span>{" "}
                <span className="font-mono text-xs">{promoter?.email}</span>
              </div>
              <div>
                <span className="text-neutral-500">Phone:</span> {promoter?.phone || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Surveys list */}
      <div>
        <h2 className="font-display font-bold text-xl tracking-tight mb-3">
          Customer surveys ({surveys.length})
          {surveys.reduce((a, s) => a + (s.giveaway_cases || 0), 0) > 0 && (
            <span className="ml-3 text-sm font-normal text-emerald-700" data-testid="shift-cases-total">
              🎁 {surveys.reduce((a, s) => a + (s.giveaway_cases || 0), 0)} silicone case(s) given
            </span>
          )}
        </h2>
        {surveys.length === 0 ? (
          <div className="glass-panel p-4 text-sm text-neutral-500">
            No surveys logged.
          </div>
        ) : (
          <div className="glass-panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/50 border-b border-slate-200/70">
                <tr>
                  {[
                    "Time",
                    "Nationality",
                    "Heard",
                    "Current",
                    "Bought",
                    "Units",
                    "Nicotine",
                    "Flavor",
                    "Feedback",
                    "Giveaway",
                    "",
                  ].map((h, i) => (
                    <th
                      key={h || i}
                      className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {surveys.map((s) => (
                  <tr key={s.id} className="border-t border-slate-200/70">
                    <td className="p-3 font-mono text-xs">
                      {fmtTime(s.created_at)}
                    </td>
                    <td className="p-3">{s.customer_nationality}</td>
                    <td className="p-3">{s.heard_about_ubbs}</td>
                    <td className="p-3">{s.current_product}</td>
                    <td className="p-3 text-xs">{fmtBought(s)}</td>
                    <td className="p-3 font-mono font-semibold" data-testid={`shift-survey-units-${s.id}`}>
                      {unitsBought(s)}
                    </td>
                    <td className="p-3">{s.nicotine_level}</td>
                    <td className="p-3">{s.flavor || "—"}</td>
                    <td className="p-3 max-w-[300px] text-xs text-neutral-700 truncate">
                      {s.feedback || "—"}
                    </td>
                    <td className="p-3" data-testid={`shift-survey-giveaway-${s.id}`}>
                      {s.giveaway_cases > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
                          {s.giveaway_cases}
                          <span className="text-[10px] font-normal text-neutral-400">
                            {s.giveaway_reason === "competitor_switcher" ? "switcher" : "std"}
                          </span>
                        </span>
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => setEditSurvey(s)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-slate-300/70 hover:bg-white/80"
                        data-testid={`edit-survey-${s.id}`}
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SurveyEditDialog
        survey={editSurvey}
        onClose={() => setEditSurvey(null)}
        onSaved={() => {
          setEditSurvey(null);
          load();
        }}
      />
    </div>
  );
}
